// Régua de Atraso - Bloco 9 Aprimorado
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Clock, Calendar, AlertTriangle, CheckCircle, Timer, 
  TrendingUp, Loader2, CalendarDays
} from 'lucide-react';

const API_URL = window.location.origin;

const STATUS_CONFIG = {
  no_prazo: { label: 'No Prazo', color: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle },
  proximo: { label: 'Próximo', color: 'bg-yellow-100 text-yellow-700 border-yellow-300', icon: Timer },
  tolerancia: { label: 'Tolerância', color: 'bg-orange-100 text-orange-700 border-orange-300', icon: Clock },
  atrasado: { label: 'Atrasado', color: 'bg-red-100 text-red-700 border-red-300', icon: AlertTriangle }
};

export function ReguaAtraso({ year, trimestre, filtros }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [turmaId, setTurmaId] = useState('');

  useEffect(() => {
    loadData();
  }, [year, trimestre, turmaId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        year: year.toString(),
        trimestre
      });
      if (turmaId) params.append('class_group_id', turmaId);
      
      const res = await fetch(`${API_URL}/api/pendencias-academicas/regua-atraso?${params}`);
      const json = await res.json();
      setData(json);
    } catch (error) {
      console.error('Erro ao carregar régua:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!data) return null;

  const { periodo, tipos, resumo } = data;

  return (
    <div className="space-y-6" data-testid="regua-atraso">
      {/* Header com Timeline do Trimestre */}
      <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/20 rounded-xl">
                <CalendarDays className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Régua de Prazos</h2>
                <p className="text-purple-200">{trimestre} {year}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{periodo.percentual_tempo}%</div>
              <div className="text-sm text-purple-200">do período decorrido</div>
            </div>
          </div>
          
          {/* Barra de Progresso do Tempo */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-purple-200">
              <span>{periodo.inicio}</span>
              <span>Hoje: {periodo.hoje}</span>
              <span>{periodo.fim}</span>
            </div>
            <div className="relative h-4 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="absolute h-full bg-white/80 rounded-full transition-all duration-500"
                style={{ width: `${periodo.percentual_tempo}%` }}
              />
              <div 
                className="absolute h-full w-1 bg-white"
                style={{ left: `${periodo.percentual_tempo}%` }}
              />
            </div>
            <div className="text-center text-sm text-purple-200">
              {periodo.dias_decorridos} de {periodo.dias_totais} dias
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filtro de Turma */}
      <Card className="border-purple-100">
        <CardContent className="pt-4">
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">Filtrar por turma:</span>
            <Select value={turmaId || "all"} onValueChange={(v) => setTurmaId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[200px] bg-white border-purple-200">
                <SelectValue placeholder="Todas as turmas" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-[300px]">
                <SelectItem value="all">Todas as turmas</SelectItem>
                {(filtros?.turmas || []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Resumo de Status */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-medium text-green-700">No Prazo</span>
            </div>
            <div className="text-3xl font-bold text-green-600 mt-2">{resumo.no_prazo}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Timer className="w-5 h-5 text-yellow-600" />
              <span className="font-medium text-yellow-700">Próximo</span>
            </div>
            <div className="text-3xl font-bold text-yellow-600 mt-2">{resumo.proximo}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-600" />
              <span className="font-medium text-orange-700">Tolerância</span>
            </div>
            <div className="text-3xl font-bold text-orange-600 mt-2">{resumo.tolerancia}</div>
          </CardContent>
        </Card>
        
        <Card className="bg-red-50 border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <span className="font-medium text-red-700">Atrasado</span>
            </div>
            <div className="text-3xl font-bold text-red-600 mt-2">{resumo.atrasado}</div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Tipos com Régua */}
      <Card className="border-purple-100">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            Status por Tipo de Avaliação
          </CardTitle>
          <CardDescription>
            Acompanhe o prazo de cada tipo de avaliação
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {tipos.map((tipo, idx) => {
              const config = STATUS_CONFIG[tipo.status] || STATUS_CONFIG.no_prazo;
              const StatusIcon = config.icon;
              
              return (
                <div key={idx} className={`p-4 rounded-xl border-2 ${config.color}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <StatusIcon className="w-5 h-5" />
                      <div>
                        <span className="font-semibold">{tipo.tipo}</span>
                        {tipo.is_obrigatorio && (
                          <Badge className="ml-2 bg-purple-500 text-xs">Obrigatório</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span>Prazo: {tipo.data_limite}</span>
                      {tipo.dias_restantes > 0 ? (
                        <Badge variant="outline" className="border-green-500 text-green-700">
                          {tipo.dias_restantes} dias restantes
                        </Badge>
                      ) : tipo.dias_atraso > 0 ? (
                        <Badge className="bg-red-500">
                          {tipo.dias_atraso} dias em atraso
                        </Badge>
                      ) : (
                        <Badge className="bg-yellow-500">Hoje é o prazo</Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-slate-600">
                      <span>Progresso do prazo</span>
                      <span>{tipo.percentual_prazo}%</span>
                    </div>
                    <Progress 
                      value={Math.min(100, tipo.percentual_prazo)} 
                      className={`h-2 ${
                        tipo.status === 'no_prazo' ? '[&>div]:bg-green-500' :
                        tipo.status === 'proximo' ? '[&>div]:bg-yellow-500' :
                        tipo.status === 'tolerancia' ? '[&>div]:bg-orange-500' : '[&>div]:bg-red-500'
                      }`}
                    />
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Margem: {tipo.margem_tolerancia} dias</span>
                      <span>{tipo.notas_lancadas} notas lançadas</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
